/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Breadcrumbs as CDSBreadcrumbs, Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';

import { COLLECTION_TYPE } from 'bundles/enterprise-collections/utils/SingleProgramConstants';
import Skeleton from 'bundles/unified-home-common/components/shared/Skeleton';

import _t from 'i18n!nls/enterprise-collections';

type PropsFromCaller = {
  programSlug: string;
  thirdPartyOrgSlug: string;
  thirdPartyOrganizationId: string;
  collectionType: COLLECTION_TYPE;
  programName: string;
  title?: string;
  loading?: boolean;
  isOrgScoped?: boolean;
};

type Props = PropsFromCaller;

export const useStyles = () => {
  return {
    container: css`
      background-color: var(--cds-color-grey-25);
      display: grid;
      width: 100%;

      .breadcrumb {
        margin: 0 auto;
        padding: var(--cds-spacing-150) 0;
      }
    `,
    refreshedContainer: css`
      background-color: var(--cds-color-neutral-background-primary);

      a {
        color: var(--cds-color-grey-600);
        font-weight: 400;
      }

      ${breakpoints.down('lg')} {
        padding: 0 var(--cds-spacing-150);
      }
      ${breakpoints.down('xs')} {
        padding: 0;
      }
    `,
    separator: css`
      margin: 0 var(--cds-spacing-100);
      display: block;
    `,
    gridContainer: css`
      padding-top: 0 !important; /* remove padding from CDS Grid */
    `,
    gridItem: css`
      width: 100%;
    `,
  };
};

export const CollectionsBreadcrumbs = ({
  programSlug,
  collectionType,
  programName,
  thirdPartyOrgSlug,
  thirdPartyOrganizationId,
  title,
  loading = false,
  isOrgScoped = false,
}: Props): JSX.Element => {
  const styles = useStyles();

  const COLLECTION_TITLE = {
    [COLLECTION_TYPE.CURATED]: _t('Collections'),
    [COLLECTION_TYPE.BROWSE]: _t('All recommendations by Coursera'),
    [COLLECTION_TYPE.TAILORED_LEARNING]: _t('Tailored Learning'),
  };

  const homeHref = isOrgScoped ? `/organizations/${thirdPartyOrgSlug}` : `/programs/${programSlug}`;
  const collectionTypeLabel = COLLECTION_TITLE[collectionType];

  const refreshedBreadcrumbs = isOrgScoped ? (
    <CDSBreadcrumbs role="list" variant="link">
      <CDSBreadcrumbs.Item role="listitem" href={homeHref}>
        <Typography2 variant="bodySecondary" component="a" color="supportText">
          {programName}
        </Typography2>
      </CDSBreadcrumbs.Item>
      <CDSBreadcrumbs.Item role="listitem" href={`/organizations/${thirdPartyOrgSlug}/view-all-collections/curated`}>
        <Typography2 variant="bodySecondary" component="a" color="supportText">
          {collectionTypeLabel}
        </Typography2>
      </CDSBreadcrumbs.Item>
      <CDSBreadcrumbs.Item role="listitem">
        <Typography2 variant="bodySecondary" component="p" color="supportText">
          {loading ? <Skeleton fixedWidth={150} /> : <>{title || collectionTypeLabel}</>}
        </Typography2>
      </CDSBreadcrumbs.Item>
    </CDSBreadcrumbs>
  ) : (
    <CDSBreadcrumbs role="list" variant="link">
      <CDSBreadcrumbs.Item role="listitem" href={homeHref}>
        <Typography2 variant="bodySecondary" component="a" color="supportText">
          {programName}
        </Typography2>
      </CDSBreadcrumbs.Item>
      <CDSBreadcrumbs.Item role="listitem">
        <Typography2 variant="bodySecondary" component="p" color="supportText">
          {loading ? <Skeleton fixedWidth={150} /> : <>{title || collectionTypeLabel}</>}
        </Typography2>
      </CDSBreadcrumbs.Item>
    </CDSBreadcrumbs>
  );
  return (
    <div css={[styles.container, styles.refreshedContainer]}>
      <PageGridContainer css={styles.gridContainer}>
        <Grid item xs={12} css={styles.gridItem}>
          <Typography2 component="h4" variant="subtitleMedium" color="supportText">
            {refreshedBreadcrumbs}
          </Typography2>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default CollectionsBreadcrumbs;
